import { Injectable, isDevMode } from '@angular/core';
import { DevAWSSettings, ProdAWSSettings } from 'src/awsconfig';
import { UsersService } from '../users/users.service';
import { CrmSearchResult } from './crm-search/search-result.model';
import { SalesMailCrm, SalesMailCrms, SHERPA_CRM } from "../static/crm.static";
import { AppUser } from '../users/users';
import { API } from 'aws-amplify';
import { ApiResponse } from '../shared-models/api-response.model';
import { CrmSherpaCounselor } from './crm.interfaces';
import { SherpaSearchResult } from './sherpa.model';

export interface CrmSalesMailNavigationState {
  crm_id: number;
  salesmail_id?: number; //Optional because can be used before a sm has been created,
  crm_recipient_id: string;
  crm_recipient_email?: string;
  crm_recipient_first_name?: string;
  crm_recipient_last_name?: string;
}

/**
 * General Interface for posting to the various CRM's, will likely get additional props over time
 * Each crm implementation interface will be responsible for ensuring the required data exists init
 */
export interface CrmPostProps {  
  shortUrl: string;       // SalesMail Short URL
  prospectId?: string      // ID of the CRM Prospect / User being send to
  videoDuration?: number;  // Duration of the SM
  videoName?: string;      // AI or user entered name of the video
  recipient_name?: string;  // Name string to use when posting 
}

@Injectable({
  providedIn: 'root'
})
export class CrmService {

  readonly apiName = "SalesMailUser"
  readonly apiVersion = "2016-04-18";
  readonly awsRegion = "us-east-1";
  readonly crms = SalesMailCrms;
  readonly setting = isDevMode() ? new DevAWSSettings() : new ProdAWSSettings();

  private _hasInit = false; // Used to track init state
  private _userHasSherpaAccess = false; // Used internally to track sherpa access, set during init
  private _userSherpaCounselor: CrmSherpaCounselor | undefined = undefined; // Set on init Sherpa, tied to SM user by email
  
  constructor(private userService: UsersService) {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Public interface to all CRMS ////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Initializes CRM calls when required based on user
   */
  async initCRM() {
    const user = await this.userService.getCurrentSalesMailUser();
    this.reset();

    switch (user.crm_id) {
      case SHERPA_CRM:
        await this.initSherpa(user);
        break;
    
      default:        
        break;
    }

    this._hasInit = true;
  }

  // Used for resetting state variables internally
  private reset() {
    this._hasInit = false;
    this._userHasSherpaAccess = false;
    this._userSherpaCounselor = undefined;
  }

  /**
   * Used for checking the init status on some corner cases
   * @returns boolean
   */
  hasInit(): Boolean {
    return this._hasInit;
  }

  /**
   * Checks to see if the user should be able to access CRM based on user.crm_id
   * Most should if set, but some like Sherpa require additional checks
   * @returns boolean
   */
  async hasCrmAccess(): Promise<boolean> {
    const user = await this.userService.getCurrentSalesMailUser();
    switch (user.crm_id) {
      case SHERPA_CRM:
        return this._userHasSherpaAccess;
        
      default:
        return true;
    }
  }

  /**
   * Determines which CRM to search based on the current authenticated users CRM settings 
   * @param searchString String to search for 
   * @returns an array of CrmSearchResult records, or an empty array should the crm not be supported
   */
  async searchCrm(searchString: string): Promise<CrmSearchResult[]> {
    const user = await this.userService.getCurrentSalesMailUser();
    const trimmedString = searchString.trim();
    
    switch (user.crm_id) {
      case SHERPA_CRM:
        return await this.searchSherpaCrm(trimmedString);
    
      default:
        return [];        
    }
  }

  /**
   * Determines which CRM to search based on the current authenticated users CRM settings 
   * @param id id of the person or prospect to get influencers for
   * @returns an array of CrmSearchResult records, or an empty array should the crm not be supported
   */
  async getInfluencers(search_record: CrmSearchResult): Promise<CrmSearchResult[]> {
    const user = await this.userService.getCurrentSalesMailUser();
    switch (user.crm_id) {
      case 3:
        return await this.getSherpaInfluencers(search_record);
    
      default:
        return [];        
    }
  }

  /**
   * Helper function to get crm name
   * @param id crm_id 
   * @returns object with id & name 
   */
  getCrmById(id: number): SalesMailCrm {
    return this.crms.find(c => c.id == id);
  }

  /**
   * Helper function that 
   * @returns an array of CRM id's that do not use email sharing
   */
  getCrmsWithNoShareButton(): number[] {
    // Currently only Sherpa. We can get fancy here if need be when/if there are more.
    // View crms.static.ts for full list of CRMS
    return [SHERPA_CRM];
  }

  async postToCrm(props: CrmPostProps): Promise<any> {
    const user = await this.userService.getCurrentSalesMailUser();
    switch (user.crm_id) {
      case 3:                
        return await this.postToSherpa(props, user);
    
      default:
        return;        
    }
  }


  ////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///////////// Sherpa ///////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * Initializes sherpa by calling the cousler function and seeing if the current user
   * email exists within the returned array. If it does not, sets flag for no crm access
   * @param user current sm user with crm details
   * @returns nothing
   */
  private async initSherpa(user: AppUser) {
    if (user.crm_id !== SHERPA_CRM) {
      return;
    }

    const path = '/sherpa/counselors';
    const init = {
      queryStringParameters: {
        'api_key': user.crm_api_key,
        'company_id': user.crm_company_id,
        'community_id': user.crm_community_id,
        'crm_url': user.crm_url ?? '',
      }
    };

    try {
      this._userHasSherpaAccess = false; // default value
      const { value }  = await API.get(this.apiName, path, init) as ApiResponse<CrmSherpaCounselor[]>
      this._userSherpaCounselor = value.find((c: CrmSherpaCounselor) => c.email === user.email) as CrmSherpaCounselor;
      this._userHasSherpaAccess = this._userSherpaCounselor !== undefined;
    } catch (error) {
      // Treat errors like no access      
      console.error(error);
    }    
  }

  /**
   * Searches the Sherpa CRM via HL api
   * @param searchString String to search for, Sherpa should support partial matches
   * @returns an array of normalized search results
   */
  private async searchSherpaCrm(searchString: string): Promise<CrmSearchResult[]> {
    const user = await this.userService.getCurrentSalesMailUser();
    const path = '/sherpa/prospect/search';
    const init = {
      queryStringParameters: {
        'api_key': user.crm_api_key,
        'company_id': user.crm_company_id,
        'community_id': user.crm_community_id,
        'crm_url': user.crm_url ?? '',
        'detailed': true,
        'pageSize': 100,
        'pageNumber': 1
      }
    };

    try {
      // Search first name
      const firstInit = {
        ...init,
        queryStringParameters: {
          ...init.queryStringParameters,
          firstName: searchString
        }
      };
      const firstNameResponse = await API.get(this.apiName, path, firstInit) as ApiResponse<SherpaSearchResult>;
      
      // Search last name
      const lastInit = {
        ...init,
        queryStringParameters: {
          ...init.queryStringParameters,
          lastName: searchString
        }
      };
      const lastNameResponse = await API.get(this.apiName, path, lastInit) as ApiResponse<SherpaSearchResult>;

      //Combine results and transform to standard response
      const values = [...(firstNameResponse.value as SherpaSearchResult[]), ...(lastNameResponse.value as SherpaSearchResult[])];      
      const transformed: CrmSearchResult[] = values
        .flatMap(result =>                   
            result.people.map(person => {  
            const email = person?.emailAddresses?.[0]?.emailAddress ?? '';
            const phone = person?.phoneNumbers?.[0]?.phoneNumber ?? '';

            return {            
                id: result.id.toString(), //Prospect id, not the person id....
                first_name: person.firstName,
                last_name: person.lastName,
                email: email,
                phone_number: phone,
                status: result.status,
                crm_data: result
              };
            }
          )
        );

      return transformed as CrmSearchResult[];
    } catch (error) {
      // Treat errors like no access      
      console.error(error);
    }    
  }

  /**
   * Get's influencers from crm crm data or api
   * @param prospect_id id to use when calling the sherpa api
   * @param search_record this is checked first to see if associated influencers already exist
   * @returns returns influencers if they exist on the search record, else calls the api to retrieve them
   */
  private async getSherpaInfluencers(search_record: CrmSearchResult): Promise<CrmSearchResult[]> {
console.debug("search sherpa:", search_record);
    // If the influeners are already in the crm data, return it
    const sherpa_data = search_record.crm_data as SherpaSearchResult;
    if (sherpa_data && sherpa_data?.influencers?.length > 0) {
      const mapped: CrmSearchResult[] = sherpa_data.influencers.map(i => {
        const email = i?.emailAddresses?.[0]?.emailAddress ?? '';
        const phone = i?.phoneNumbers?.[0]?.phoneNumber ?? '';
        return {
          id: search_record.id.toString(),
          first_name: i.firstName,
          last_name: i.lastName,
          email: email,
          phone_number: phone,
          crm_data: i
        }
      });
      return mapped;
    }
    
    // not found, call the api
    const user = await this.userService.getCurrentSalesMailUser();
    const path = '/sherpa/prospect/influencers';
    const init = {
      queryStringParameters: {
        'api_key': user.crm_api_key,
        'company_id': user.crm_company_id,
        'community_id': user.crm_community_id,
        'crm_url': user.crm_url ?? '',
        'prospect_id': search_record.id        
      }
    };
    
    try {
      const { value }  = await API.get(this.apiName, path, init) as ApiResponse<any[]>
      return value as CrmSearchResult[];
    } catch (error) {
      // Treat errors like no access      
      console.error(error);
    }  
  }

  private async postToSherpa(props: CrmPostProps, user: AppUser): Promise<any>  {

    const videoName = props.videoName ? `- ${props.videoName} -` : '';
    const recipient_name = props.recipient_name ? `to ${props.recipient_name}` : '';
    const postData = {
      description: `HeartLegacy SalesMail ${recipient_name} ${videoName} ${props.shortUrl}`,
      advance: "Continuation",
      activity: [{
        activityTypeName: "Video Message",
        leasingCounselorIds: [this._userSherpaCounselor.id],
        duration: `0:${props.videoDuration}`
      }]
    };    

    const path = '/sherpa/salesnote';
    const init = {
      body: {
        api_key: user.crm_api_key,
        community_id: user.crm_community_id,
        company_id: user.crm_company_id,
        prospect_id: props.prospectId,
        post_data: postData,
        crm_url: user.crm_url ?? ''
      }
    };
    console.debug("posting to sherpa", init);
   return API.post(this.apiName, path, init);

  }


}
